import { useUploadProgress } from '../../../hooks/store/useUploadProgress';
import { Box, Button, Typography } from '@mui/material';
import LinearProgress, {
  linearProgressClasses,
} from '@mui/material/LinearProgress';
import ErrorIcon from '@mui/icons-material/Warning';
import RetryIcon from '@mui/icons-material/Replay';
import { ScanStatus } from '../../../type';
import { useCallback } from 'react';
import {
  cancelUpload,
  useResendFile,
} from '../../../hooks/firebase/useSendFiles';
import { useIntl } from 'react-intl';

type MessageUploadStatusProps = {
  channelGuid: string;
  messageGuid: string;
  scanStatus?: ScanStatus;
};

export const MessageUploadStatus = (props: MessageUploadStatusProps) => {
  const { channelGuid, messageGuid, scanStatus } = props;
  const intl = useIntl();
  const cancel = useCallback(
    () => cancelUpload(channelGuid, messageGuid),
    [channelGuid, messageGuid],
  );
  const resend = useResendFile(channelGuid, messageGuid);
  const uploadProgress = useUploadProgress(channelGuid, messageGuid);
  if (!uploadProgress) {
    return null;
  }
  const [state, progress] = uploadProgress;
  if (state === 'success' && scanStatus !== 'NOT_STARTED') {
    return null;
  } else if (state === 'success' || state === 'running') {
    const progressValue = state === 'success' ? 100 : progress ?? 0;
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          mt: 2,
          width: 200,
        }}
      >
        <LinearProgress
          variant='determinate'
          value={Math.min(progressValue, 95)}
          sx={{
            mr: 4,
            width: '100%',
            height: 8,
            borderRadius: 1,
            [`&.${linearProgressClasses.colorPrimary}`]: {
              backgroundColor: '#d8dade',
            },
            [`& .${linearProgressClasses.barColorSecondary}`]: {
              borderRadius: 1,
            },
          }}
        />
        <Button onClick={cancel}>
          <Typography sx={{ fontSize: 12 }}>Cancel</Typography>
        </Button>
      </Box>
    );
  }
  if (state === 'error') {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          mt: 2,
          width: 200,
        }}
      >
        <ErrorIcon
          sx={{ width: 20, height: 20, color: '#d00070', mr: 'auto' }}
        />
        <Button
          onClick={resend}
          startIcon={<RetryIcon width={20} height={20} />}
          sx={{ fontSize: 12, fontWeight: 'normal' }}
        >
          {intl.formatMessage({ id: 'message.resend' })}
        </Button>
      </Box>
    );
  }
  return null;
};
